import { useAuth, useClerk, useUser } from "@clerk/react";

export type CallerPrincipals = {
  userPrincipalId: string | null;
  principalIds: string[];
};

export type AuthedFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type TokenProvider = () => Promise<string | null | undefined> | string | null | undefined;
type ClerkGlobal = {
  signOut?: () => Promise<void>;
  session?: {
    getToken?: () => Promise<string | null>;
  } | null;
};

let tokenProvider: TokenProvider | null = null;

function defaultFetch(): typeof fetch {
  return globalThis.fetch.bind(globalThis);
}

function getClerkGlobal(): ClerkGlobal | undefined {
  return (globalThis as typeof globalThis & { Clerk?: ClerkGlobal }).Clerk;
}

async function getClerkSessionToken(): Promise<string | null> {
  return (await getClerkGlobal()?.session?.getToken?.()) ?? null;
}

export function setCloudAuthTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider;
}

export function useCurrentUser() {
  const { isLoaded, isSignedIn, user } = useUser();
  return {
    isLoaded,
    isSignedIn: Boolean(isSignedIn),
    user: user ?? null,
    userId: user?.id ?? null,
  };
}

export function useCallerPrincipals(): CallerPrincipals {
  const { userId } = useAuth();
  return {
    userPrincipalId: userId ?? null,
    principalIds: userId ? [userId] : [],
  };
}

export async function signOut(): Promise<void> {
  const clerk = getClerkGlobal();
  if (!clerk?.signOut) return;
  await clerk.signOut();
}

export function useCloudSignOut(): () => Promise<void> {
  const clerk = useClerk();
  return () => clerk.signOut();
}

export function getAuthedFetch(options?: {
  getToken?: TokenProvider;
  fetchImpl?: typeof fetch;
}): AuthedFetch {
  const getToken = options?.getToken ?? tokenProvider ?? getClerkSessionToken;
  const fetchImpl = options?.fetchImpl ?? defaultFetch();

  return async (input, init = {}) => {
    const token = await getToken();
    const headers = new Headers(init.headers);
    if (token) headers.set("authorization", `Bearer ${token}`);
    return fetchImpl(input, { ...init, headers });
  };
}
